import { Button, Modal, Icon } from "@aidkitorg/component-library";
import { DashboardBarChart, DashboardMetric, DashboardPieChart, DashboardSummaryTable, SupportedDashboardComponent, DashboardReport, DashboardLineChart } from "@aidkitorg/types/lib/admin-panel/dashboard";
import React from "react";
import { NavLink } from "react-router-dom";
import { useMetricFormState } from "../../hooks/useMetricFormState";
import { MetricFormFields } from "../MetricFormFields";
import { usePieChartFormState } from "../../hooks/usePieChartFormState";
import { PieChartFormFields } from "../PieChartFormFields";
import { useUpdateComponentById } from "../../hooks/useUpdateComponentById";
import { useBarChartFormState } from "../../hooks/useBarChartFormState";
import { BarChartFormFields } from "../BarChartFormFields";
import { Map } from "@aidkitorg/types/lib/survey";
import { useMapFormState } from "../../hooks/useMapFormState";
import { MapFormFields } from "../MapFormFields";
import { useSummaryTableFormState } from "../../hooks/useSummaryTableFormState";
import { SummaryTableFormFields } from "../SummaryTableFormFields";
import { useLineChartFormState } from "../../hooks/useLineChartFormState";
import { LineChartFormFields } from "../LineChartFormFields";
import { useApplicantCountFormState } from "../../hooks/useApplicantCountFormState";
import { ApplicantCountFormFields } from "../ApplicantCountFormFields";
import { useLocalizedStrings } from "../../../../Localization";
import { useDashboards } from "../../../../Contexts/DashboardsContext";

type ComponentEditorModalProps = {
  id: string;
  component: SupportedDashboardComponent,
  isOpen: boolean,
  onClose: () => void,
  currentTitle?: string,
}

type MetricComponentEditorProps = {
  id: string;
  component: DashboardMetric;
  onClose: () => void;
};

export const MetricComponentEditor: React.FC<MetricComponentEditorProps> = ({
  id,
  component,
  onClose,
}) => {
  const L = useLocalizedStrings()

  const {
    title, setTitle,
    description, setDescription,
    metricType, setMetricType,
    aggregation, setAggregation,
    dataField, setDataField,
    numeratorAggregation, setNumeratorAggregation,
    numerator, setNumerator,
    denominatorAggregation, setDenominatorAggregation,
    denominator, setDenominator,
    decimalPlaces, setDecimalPlaces,
    filterConfig, setFilterConfig,
    buildMetricObject,
    isFormStateValid,
  } = useMetricFormState(component)

  const updateComponentById = useUpdateComponentById();
  const { getDashboardComponentTranslation } = useDashboards();

  const saveMetricComponent = async () => {
    const updatedMetric = buildMetricObject();

    const translations = await getDashboardComponentTranslation(updatedMetric);
    if (translations && translations.success) {
      updateComponentById(id, updatedMetric)
      onClose();
    } else {
      console.error("Failed to translate metric component:", updatedMetric);
      // TODO: Add user-facing error message
    }
  };

  return (
    <div className="flex flex-col gap-y-2">
      <MetricFormFields
        title={title}
        setTitle={setTitle}
        metricType={metricType}
        setMetricType={setMetricType}
        aggregation={aggregation}
        setAggregation={setAggregation}
        dataField={dataField}
        setDataField={setDataField}
        numeratorAggregation={numeratorAggregation}
        setNumeratorAggregation={setNumeratorAggregation}
        numerator={numerator}
        setNumerator={setNumerator}
        denominatorAggregation={denominatorAggregation}
        setDenominatorAggregation={setDenominatorAggregation}
        denominator={denominator}
        setDenominator={setDenominator}
        decimalPlaces={decimalPlaces}
        setDecimalPlaces={setDecimalPlaces}
        description={description}
        setDescription={setDescription}
        filterConfig={filterConfig}
        setFilterConfig={setFilterConfig}
        isEditMode={true}
      />

      <div className="flex justify-between gap-x-2 pt-2">
        <Button onClick={onClose} className="!w-1/2" variant="secondary">
          {L.admin_panel.dashboards.cancel}
        </Button>
        <Button isDisabled={!isFormStateValid} onClick={saveMetricComponent} className="!w-1/2">
          {L.admin_panel.dashboards.save}
        </Button>
      </div>
    </div>
  );
};

type ApplicantCountComponentEditorProps = {
  id: string;
  component: DashboardMetric;
  onClose: () => void;
};

export const ApplicantCountComponentEditor: React.FC<ApplicantCountComponentEditorProps> = ({
  id,
  component,
  onClose,
}) => {
  const L = useLocalizedStrings()

  const {
    title,
    setTitle,
    filterConfig,
    setFilterConfig,
    buildApplicantCountObject,
    isFormStateValid,
  } = useApplicantCountFormState(component)

  const updateComponentById = useUpdateComponentById();
  const { getDashboardComponentTranslation } = useDashboards();

  const saveApplicantCountComponent = async () => {
    const updatedApplicantCount = buildApplicantCountObject();

    const translations = await getDashboardComponentTranslation(updatedApplicantCount);
    if (translations && translations.success) {
      updateComponentById(id, updatedApplicantCount)
      onClose();
    } else {
      console.error("Failed to translate applicant count component:", updatedApplicantCount);
      // TODO: Add user-facing error message
    }
  };

  return (
    <div className="flex flex-col gap-y-2">
      <ApplicantCountFormFields
        title={title}
        setTitle={setTitle}
        filterConfig={filterConfig}
        setFilterConfig={setFilterConfig}
        isEditMode={true}
      />

      <div className="flex justify-between gap-x-2 pt-2">
        <Button onClick={onClose} className="!w-1/2" variant="secondary">
          {L.admin_panel.dashboards.cancel}
        </Button>
        <Button isDisabled={!isFormStateValid} onClick={saveApplicantCountComponent} className="!w-1/2">
          {L.admin_panel.dashboards.save}
        </Button>
      </div>
    </div>
  );
};

type PieChartComponentEditorProps = {
  id: string,
  component: DashboardPieChart,
  onClose: () => void
}

export const PieChartComponentEditor: React.FC<PieChartComponentEditorProps> = ({
  id,
  component,
  onClose
}) => {
  const L = useLocalizedStrings()

  const {
    title, setTitle,
    description, setDescription,
    groupDataField, setGroupDataField,
    aggregation, setAggregation,
    dataField, setDataField,
    filterConfig, setFilterConfig,
    buildPieChartObject,
    isFormStateValid
  } = usePieChartFormState(component)

  const updateComponentById = useUpdateComponentById();
  const { getDashboardComponentTranslation } = useDashboards();

  const savePieChartComponent = async () => {
    const updatedMetric = buildPieChartObject()

    const translations = await getDashboardComponentTranslation(updatedMetric);
    if (translations && translations.success) {
      updateComponentById(id, updatedMetric)
      onClose()
    } else {
      console.error("Failed to translate pie chart component:", updatedMetric);
      // TODO: Add user-facing error message
    }
  }

  return <div className="flex flex-col gap-y-2">
    <PieChartFormFields
      title={title}
      setTitle={setTitle}
      description={description}
      setDescription={setDescription}
      groupDataField={groupDataField}
      setGroupDataField={setGroupDataField}
      aggregation={aggregation}
      setAggregation={setAggregation}
      dataField={dataField}
      setDataField={setDataField}
      filterConfig={filterConfig}
      setFilterConfig={setFilterConfig}
    />
    <div className="flex justify-between gap-x-2 pt-2">
      <Button onClick={onClose} className="!w-1/2" variant="secondary">
        {L.admin_panel.dashboards.cancel}
      </Button>
      <Button isDisabled={!isFormStateValid} onClick={savePieChartComponent} className="!w-1/2">
        {L.admin_panel.dashboards.save}
      </Button>
    </div>
  </div>
}

type BarChartComponentEditorProps = {
  id: string,
  component: DashboardBarChart,
  onClose: () => void
}

export const BarChartComponentEditor: React.FC<BarChartComponentEditorProps> = ({
  id,
  component,
  onClose
}) => {
  const L = useLocalizedStrings()

  const {
    title, setTitle,
    description, setDescription,
    xAxis, setXAxis,
    yAxis, setYAxis,
    filterConfig, setFilterConfig,
    buildBarChartObject,
    isFormStateValid
  } = useBarChartFormState(component)

  const updateComponentById = useUpdateComponentById();
  const { getDashboardComponentTranslation } = useDashboards();

  const saveBarChartComponent = async () => {
    const updatedMetric = buildBarChartObject()

    const translations = await getDashboardComponentTranslation(updatedMetric);
    if (translations && translations.success) {
      updateComponentById(id, updatedMetric)
      onClose()
    } else {
      console.error("Failed to translate bar chart component:", updatedMetric);
      // TODO: Add user-facing error message
    }
  }

  return <div className="flex flex-col gap-y-2">
    <BarChartFormFields 
      title={title}
      setTitle={setTitle}
      description={description}
      setDescription={setDescription}
      filterConfig={filterConfig}
      setFilterConfig={setFilterConfig}
      xAxis={xAxis}
      setXAxis={setXAxis}
      yAxis={yAxis}
      setYAxis={setYAxis}
    />
    <div className="flex justify-between gap-x-2 pt-2">
      <Button onClick={onClose} className="!w-1/2" variant="secondary">
        {L.admin_panel.dashboards.cancel}
      </Button>
      <Button isDisabled={!isFormStateValid} onClick={saveBarChartComponent} className="!w-1/2">
        {L.admin_panel.dashboards.save}
      </Button>
    </div>
  </div>
}

type MapComponentEditorProps = {
  id: string,
  component: Map,
  onClose: () => void,
}

export const MapComponentEditor: React.FC<MapComponentEditorProps> = ({
  id,
  component,
  onClose
}) => {
  const L = useLocalizedStrings()

  const {
    title, setTitle,
    description, setDescription,
    geographicDataOption, setGeographicDataOption,
    addressField, setAddressField,
    geoJSONUrl, setGeoJSONUrl,
    filterConfig, setFilterConfig,
    buildMapObject
  } = useMapFormState(component)

  const updateComponentById = useUpdateComponentById();
  const { getDashboardComponentTranslation } = useDashboards();

  const saveMapComponent = async () => {
    const updatedMetric = buildMapObject()

    const translations = await getDashboardComponentTranslation(updatedMetric);
    if (translations && translations.success) {
      updateComponentById(id, updatedMetric)
      onClose()
    } else {
      console.error("Failed to translate map component:", updatedMetric);
      // TODO: Add user-facing error message
    }
  }

  return <div className="flex flex-col gap-y-2">
    <MapFormFields
      title={title}
      setTitle={setTitle}
      description={description}
      setDescription={setDescription}
      geographicDataOption={geographicDataOption}
      addressField={addressField}
      setAddressField={setAddressField}
      setGeographicDataOption={setGeographicDataOption}
      geoJSONUrl={geoJSONUrl}
      setGeoJSONUrl={setGeoJSONUrl}
      filterConfig={filterConfig}
      setFilterConfig={setFilterConfig}
    />
    <div className="flex justify-between gap-x-2 pt-2">
      <Button onClick={onClose} className="!w-1/2" variant="secondary">
        {L.admin_panel.dashboards.cancel}
      </Button>
      <Button onClick={saveMapComponent} className="!w-1/2">
        {L.admin_panel.dashboards.save}
      </Button>
    </div>
  </div>
}

type SummaryTableComponentEditorProps = {
  id: string,
  component: DashboardSummaryTable,
  onClose: () => void
}

export const SummaryTableComponentEditor: React.FC<SummaryTableComponentEditorProps> = ({
  id,
  component,
  onClose
}) => {
  const L = useLocalizedStrings()

  const {
    title, setTitle,
    description, setDescription,
    groupByConfig, onGroupByChange,
    filterConfig, setFilterConfig,
    buildSummaryTableObject,
    isFormStateValid
  } = useSummaryTableFormState(component)

  const updateComponentById = useUpdateComponentById();
  const { getDashboardComponentTranslation } = useDashboards();

  const saveSummaryTableComponent = async () => {
    const updatedSummaryTable = buildSummaryTableObject()

    const translations = await getDashboardComponentTranslation(updatedSummaryTable);
    if (translations && translations.success) {
      updateComponentById(id, updatedSummaryTable)
      onClose()
    } else {
      console.error("Failed to translate summary table component:", updatedSummaryTable);
      // TODO: Add user-facing error message
    }
  }

  return <div className="flex flex-col gap-y-2">
    <SummaryTableFormFields
      title={title}
      setTitle={setTitle}
      description={description}
      setDescription={setDescription}
      groupByConfig={groupByConfig}
      onGroupByChange={onGroupByChange}
      filterConfig={filterConfig}
      setFilterConfig={setFilterConfig}
    />
    <div className="flex justify-between gap-x-2 pt-2">
      <Button onClick={onClose} className="!w-1/2" variant="secondary">
        {L.admin_panel.dashboards.cancel}
      </Button>
      <Button isDisabled={!isFormStateValid} onClick={saveSummaryTableComponent} className="!w-1/2">
        {L.admin_panel.dashboards.save}
      </Button>
    </div>
  </div>
}

type LineChartComponentEditorProps = {
  id: string,
  component: DashboardLineChart,
  onClose: () => void
}

export const LineChartComponentEditor: React.FC<LineChartComponentEditorProps> = ({
  id,
  component,
  onClose
}) => {
  const L = useLocalizedStrings()

  const {
    title, setTitle,
    description, setDescription,
    xAxis, setXAxis,
    yAxis, setYAxis,
    filterConfig, setFilterConfig,
    buildLineChartObject,
    isFormStateValid
  } = useLineChartFormState(component)

  const updateComponentById = useUpdateComponentById();
  const { getDashboardComponentTranslation } = useDashboards();

  const saveLineChartComponent = async () => {
    const updatedLineChart = buildLineChartObject()

    const translations = await getDashboardComponentTranslation(updatedLineChart);
    if (translations && translations.success) {
      updateComponentById(id, updatedLineChart)
      onClose()
    } else {
      console.error("Failed to translate line chart component:", updatedLineChart);
      // TODO: Add user-facing error message
    }
  }

  return <div className="flex flex-col gap-y-2">
    <LineChartFormFields
      title={title}
      setTitle={setTitle}
      description={description}
      setDescription={setDescription}
      xAxis={xAxis}
      setXAxis={setXAxis}
      yAxis={yAxis}
      setYAxis={setYAxis}
      filterConfig={filterConfig}
      setFilterConfig={setFilterConfig}
    />
    <div className="flex justify-between gap-x-2 pt-2">
      <Button onClick={onClose} className="!w-1/2" variant="secondary">
        {L.admin_panel.dashboards.cancel}
      </Button>
      <Button isDisabled={!isFormStateValid} onClick={saveLineChartComponent} className="!w-1/2">
        {L.admin_panel.dashboards.save}
      </Button>
    </div>
  </div>
}

type ReportComponentEditorProps = {
  component: DashboardReport
}

export const ReportComponentEditor: React.FC<ReportComponentEditorProps> = ({
  component,
}) => {
  const L = useLocalizedStrings()

  const getReportEditUrl = () => {
    return `/admin-panel/reports/${component.reportId}/edit`
  }

  return (
    <div className="flex flex-col gap-y-4">
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="mb-4">
          <Icon name="DocumentTextIcon" type="outline" size="xl" className="text-gray-400" />
        </div>
        <div className="text-lg font-medium mb-2">
          {L.admin_panel.dashboards.edit_report_in_builder}
        </div>
        <div className="text-sm text-gray-600 mb-6 max-w-sm">
          {L.admin_panel.dashboards.edit_report_in_builder_msg}
        </div>
        <NavLink
          to={getReportEditUrl()}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-3 py-2 rounded text-sm font-bold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors duration-300"
          style={{
            backgroundColor: 'var(--ak-color-button-secondary-background)',
            border: 'var(--ak-color-button-secondary-border)',
            color: 'var(--ak-color-button-secondary-text)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--ak-color-button-secondary-background-hover)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--ak-color-button-secondary-background)';
          }}
        >
          <Icon name="PencilIcon" type="solid" size="sm" />
          {L.admin_panel.dashboards.edit_report}
          <Icon name="ArrowTopRightOnSquareIcon" type="solid" size="sm" />
        </NavLink>
      </div>
    </div>
  )
}

export const ComponentEditorModal = ({ id, component, isOpen, onClose, currentTitle }: ComponentEditorModalProps) => {
  const title = currentTitle || ('title' in component && component.title ?
    (typeof component.title === 'string' ? component.title : component.title['en']) :
    'Dashboard Component')

  const renderEditor = () => {
    switch (component.kind) {
      case 'Hero Number':
        // Check if this is an applicant count component
        const isApplicantCountComponent = component.summary.kind === 'Count'

        if (isApplicantCountComponent) {
          return <ApplicantCountComponentEditor key={id} id={id} component={component} onClose={onClose} />
        }
        return <MetricComponentEditor key={id} id={id} component={component} onClose={onClose} />
      case 'Pie Chart':
        return <PieChartComponentEditor key={id} id={id} component={component} onClose={onClose} />
      case 'Bar Chart':
        return <BarChartComponentEditor key={id} id={id} component={component} onClose={onClose} />
      case 'Line Chart':
        return <LineChartComponentEditor key={id} id={id} component={component} onClose={onClose} />
      case 'Map':
        return <MapComponentEditor key={id} id={id} component={component} onClose={onClose} />
      case 'Summary Table':
        return <SummaryTableComponentEditor key={id} id={id} component={component} onClose={onClose} />
      case 'Report':
        return <ReportComponentEditor key={id} component={component} />
    }
  }

  return (
    <Modal size='sm' title={title} open={isOpen} onClose={onClose} className={'max-w-4xl'}>
      {renderEditor()}
    </Modal>
  )
}